import React, { useState } from 'react';
import { Button, Label, Modal, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getPatientBranchHistory } from '../../../api/protocol';
import { Audit as AuditIcon } from '../../../components/Icons/Icons';
import { HandleApiError } from '../../../components/Notifications/APIErrorHandler';
import { Spinner } from '../../../components/Utils/Loaders';
import { toLocaleDateTimeFormatter } from '../../../components/Utils/Formatter';
import { WithAuth } from '../../../components/Authorization/WithAuthProtocol';
import { AssignBranchModal } from './AssignBranchModal';

export const PatientBranchBadge = ({ protocolId, numberInProtocol, branch, onBranchChanged }) => {
    const { t } = useTranslation();
    const [showModal, setShowModal] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [history, setHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);

    const hasBranch = !!branch;
    const label = hasBranch ? branch.name : t('protocols.branches.sinRama');
    const style = hasBranch ? 'info' : 'default';

    const handleShowHistory = async () => {
        setShowHistory(true);
        setIsLoadingHistory(true);
        try {
            const response = await getPatientBranchHistory(protocolId, numberInProtocol);
            setHistory(response.data || []);
        } catch (error) {
            HandleApiError(error);
        } finally {
            setIsLoadingHistory(false);
        }
    };

    const _parseBranchName = (json) => {
        if (!json) return null;
        try {
            const parsed = typeof json === 'string' ? JSON.parse(json) : json;
            return parsed.BranchName ?? null;
        } catch {
            return null;
        }
    };

    return (
        <span className="patient-branch-badge" style={{ marginLeft: '8px' }}>
            <Label bsStyle={style}>{label}</Label>

            {hasBranch && (
                <WithAuth protocolId={protocolId} requiredPermission={["VisitTracking.Add"]}>
                    <button
                        type="button"
                        className="btn-link btn-sm"
                        style={{ marginLeft: '4px', fontSize: '11px' }}
                        onClick={() => setShowModal(true)}
                    >
                        {t('protocols.branches.changeBranch')}
                    </button>
                </WithAuth>
            )}

            <button
                type="button"
                className="btn-link btn-sm"
                style={{ marginLeft: '4px', fontSize: '11px' }}
                title={t('protocols.branches.branchHistory')}
                onClick={handleShowHistory}
            >
                <AuditIcon style={{ fontSize: '25px', color: '#50ABB5', cursor: 'hand' }} />
            </button>

            <AssignBranchModal
                isShowing={showModal}
                hide={() => setShowModal(false)}
                protocolId={protocolId}
                numberInProtocol={numberInProtocol}
                isChange={hasBranch}
                onSuccess={onBranchChanged}
            />

            {showHistory && (
                <Modal show onHide={() => setShowHistory(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>{t('protocols.branches.branchHistory')}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {isLoadingHistory
                            ? <Spinner />
                            : history.length === 0
                                ? <p>{t('protocols.branches.noBranchHistory')}</p>
                                : <Table striped condensed responsive>
                                    <thead>
                                        <tr>
                                            <th>{t('commons.date')}</th>
                                            <th>{t('commons.modifiedBy')}</th>
                                            <th>{t('protocols.branches.title')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {history.map((entry, i) => {
                                            const oldName = _parseBranchName(entry.oldValues);
                                            const newName = _parseBranchName(entry.newValues);
                                            return (
                                                <tr key={i}>
                                                    <td>{toLocaleDateTimeFormatter(entry.dateTime)}</td>
                                                    <td>{entry.userId}</td>
                                                    <td>
                                                        {oldName
                                                            ? <>{oldName} <span>→</span> {newName}</>
                                                            : newName}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                        }
                    </Modal.Body>
                    <Modal.Footer>
                        <Button onClick={() => setShowHistory(false)}>{t('buttons.close')}</Button>
                    </Modal.Footer>
                </Modal>
            )}
        </span>
    );
};
